import { getBuyerSignerIndex, MAX_BUYER_SIGNER_SLOTS } from "./deal-parties"
import type { MergeFieldBinding } from "./merge-fields"

export interface DraftStateOverrides {
  hasTradeIn?: boolean
  hasAddress?: boolean
  hasLender?: boolean
  hasExtraBuyer?: boolean
  includeDealerSigner?: boolean
}

export type PacketAnalysisDomainSectionKey =
  | "dealBasics"
  | "saleVehicle"
  | "tradeIn"
  | "buyers"
  | "addresses"
  | "lenderPayoff"
  | "documentFields"
  | "deliverySigners"

const BUYER_FIELD_PREFIXES = [
  "primaryBuyer",
  "buyer2",
  "buyer3",
  "buyer4",
  "buyer5",
  "buyer6",
  "buyer7",
  "buyer8",
  "buyer9",
  "buyer10",
] as const

export type PacketAnalysisBuyerDraftFieldPrefix = (typeof BUYER_FIELD_PREFIXES)[number]

type PacketAnalysisBuyerIdentityFieldSuffix =
  | "partyType"
  | "fullName"
  | "email"
  | "phone"
  | "dateOfBirth"
  | "driversLicenseNumber"
  | "driversLicenseState"
  | "driversLicenseExpiration"
  | "legalBusinessName"
  | "ein"
  | "authorizedSignerName"
  | "authorizedSignerTitle"

type PacketAnalysisBuyerAddressSectionKey =
  | "mailingAddress"
  | "registrationAddress"
  | "residenceAddress"

type PacketAnalysisBuyerAddressFieldKey = "street1" | "city" | "state" | "zipCode"

type PacketAnalysisBuyerAddressFieldSuffix =
  `${PacketAnalysisBuyerAddressSectionKey}.${PacketAnalysisBuyerAddressFieldKey}`

type PacketAnalysisBuyerOwnedDraftFieldKey =
  `${PacketAnalysisBuyerDraftFieldPrefix}.${PacketAnalysisBuyerIdentityFieldSuffix | PacketAnalysisBuyerAddressFieldSuffix}`

export type PacketAnalysisOwnedDraftFieldKey =
  | "dealBasics.saleDate"
  | "dealBasics.deliveryDate"
  | "dealBasics.storeId"
  | "dealBasics.salePriceCents"
  | "dealBasics.lineItems"
  | "saleVehicle.vin"
  | "saleVehicle.year"
  | "saleVehicle.make"
  | "saleVehicle.model"
  | "saleVehicle.stockNumber"
  | "saleVehicle.fuelType"
  | "saleVehicle.odometer"
  | "saleVehicle.odometerStatus"
  | "saleVehicle.titleStatus"
  | "tradeIn.vin"
  | "tradeIn.year"
  | "tradeIn.make"
  | "tradeIn.model"
  | "tradeIn.stockNumber"
  | "tradeIn.odometer"
  | "tradeIn.odometerStatus"
  | "tradeIn.titleStatus"
  | "tradeIn.tradeInValueCents"
  | PacketAnalysisBuyerOwnedDraftFieldKey
  | "lenderPayoff.lenderName"
  | "lenderPayoff.lenderNumber"
  | "lenderPayoff.lenderPhone"
  | "lenderPayoff.address.street"
  | "lenderPayoff.address.city"
  | "lenderPayoff.address.state"
  | "lenderPayoff.address.zipCode"
  | "lenderPayoff.lienDate"
  | "lenderPayoff.lienAmountCents"

export type PacketAnalysisOwnedDraftFieldKind = "field" | "collection"

export interface PacketAnalysisOwnedDraftField {
  key: PacketAnalysisOwnedDraftFieldKey
  label: string
  kind: PacketAnalysisOwnedDraftFieldKind
  /** Merge-field definition key that maps to this draft field (e.g., "vehicle.sale.odometer"). */
  definitionKey?: string
  /** Set at runtime when a template in the packet requires this field at send. */
  templateRequired?: boolean
}

if (BUYER_FIELD_PREFIXES.length !== MAX_BUYER_SIGNER_SLOTS) {
  throw new Error("Buyer field prefixes must align with MAX_BUYER_SIGNER_SLOTS")
}

const BUYER_IDENTITY_FIELD_DEFINITIONS: ReadonlyArray<{
  suffix: PacketAnalysisBuyerIdentityFieldSuffix
  label: string
  definitionKey?: string
}> = [
  { suffix: "partyType", label: "Party type" },
  { suffix: "fullName", label: "Full name", definitionKey: "party.full_name" },
  { suffix: "email", label: "Email", definitionKey: "party.email" },
  { suffix: "phone", label: "Phone", definitionKey: "party.phone" },
  { suffix: "dateOfBirth", label: "Date of birth", definitionKey: "party.date_of_birth" },
  {
    suffix: "driversLicenseNumber",
    label: "Driver license number",
    definitionKey: "party.drivers_license_number",
  },
  {
    suffix: "driversLicenseState",
    label: "Driver license state",
    definitionKey: "party.drivers_license_state",
  },
  {
    suffix: "driversLicenseExpiration",
    label: "Driver license expiration",
    definitionKey: "party.drivers_license_expiration",
  },
  {
    suffix: "legalBusinessName",
    label: "Legal business name",
    definitionKey: "party.legal_business_name",
  },
  { suffix: "ein", label: "EIN", definitionKey: "party.ein" },
  {
    suffix: "authorizedSignerName",
    label: "Authorized signer",
    definitionKey: "party.authorized_signer_name",
  },
  {
    suffix: "authorizedSignerTitle",
    label: "Signer title",
    definitionKey: "party.authorized_signer_title",
  },
]

const BUYER_ADDRESS_FIELD_DEFINITIONS: ReadonlyArray<{
  addressKey: PacketAnalysisBuyerAddressSectionKey
  fieldKey: PacketAnalysisBuyerAddressFieldKey
  label: string
  definitionKey: string
}> = [
  {
    addressKey: "mailingAddress",
    fieldKey: "street1",
    label: "Mailing street",
    definitionKey: "party.address.street",
  },
  {
    addressKey: "mailingAddress",
    fieldKey: "city",
    label: "Mailing city",
    definitionKey: "party.address.city",
  },
  {
    addressKey: "mailingAddress",
    fieldKey: "state",
    label: "Mailing state",
    definitionKey: "party.address.state",
  },
  {
    addressKey: "mailingAddress",
    fieldKey: "zipCode",
    label: "Mailing ZIP",
    definitionKey: "party.address.zip_code",
  },
  {
    addressKey: "registrationAddress",
    fieldKey: "street1",
    label: "Registration street",
    definitionKey: "party.address.street",
  },
  {
    addressKey: "registrationAddress",
    fieldKey: "city",
    label: "Registration city",
    definitionKey: "party.address.city",
  },
  {
    addressKey: "registrationAddress",
    fieldKey: "state",
    label: "Registration state",
    definitionKey: "party.address.state",
  },
  {
    addressKey: "registrationAddress",
    fieldKey: "zipCode",
    label: "Registration ZIP",
    definitionKey: "party.address.zip_code",
  },
  {
    addressKey: "residenceAddress",
    fieldKey: "street1",
    label: "Residence street",
    definitionKey: "party.address.street",
  },
  {
    addressKey: "residenceAddress",
    fieldKey: "city",
    label: "Residence city",
    definitionKey: "party.address.city",
  },
  {
    addressKey: "residenceAddress",
    fieldKey: "state",
    label: "Residence state",
    definitionKey: "party.address.state",
  },
  {
    addressKey: "residenceAddress",
    fieldKey: "zipCode",
    label: "Residence ZIP",
    definitionKey: "party.address.zip_code",
  },
]

function createBuyerIdentityOwnedDraftFields(
  prefix: PacketAnalysisBuyerDraftFieldPrefix,
): PacketAnalysisOwnedDraftField[] {
  return BUYER_IDENTITY_FIELD_DEFINITIONS.map((field) => ({
    key: `${prefix}.${field.suffix}` as PacketAnalysisBuyerOwnedDraftFieldKey,
    label: field.label,
    kind: "field",
    definitionKey: field.definitionKey,
  }))
}

function createBuyerAddressOwnedDraftFields(
  prefix: PacketAnalysisBuyerDraftFieldPrefix,
): PacketAnalysisOwnedDraftField[] {
  return BUYER_ADDRESS_FIELD_DEFINITIONS.map((field) => ({
    key: `${prefix}.${field.addressKey}.${field.fieldKey}` as PacketAnalysisBuyerOwnedDraftFieldKey,
    label: field.label,
    kind: "field",
    definitionKey: field.definitionKey,
  }))
}

export interface PacketAnalysisDomainSection {
  key: PacketAnalysisDomainSectionKey
  title: string
  description: string
  order: number
  ownedDraftFields: PacketAnalysisOwnedDraftField[]
  visible: boolean
  evidenceCount: number
  evidenceKeys: string[]
}

export type PacketDomainSectionDefaults = Omit<
  PacketAnalysisDomainSection,
  "visible" | "evidenceCount" | "evidenceKeys"
>

export const DOMAIN_SECTION_DEFAULTS: Record<
  PacketAnalysisDomainSectionKey,
  PacketDomainSectionDefaults
> = {
  dealBasics: {
    key: "dealBasics",
    title: "Deal Basics",
    description: "Dates, store context, pricing, and deal-level values reused across the packet.",
    order: 10,
    ownedDraftFields: [
      {
        key: "dealBasics.saleDate",
        label: "Sale date",
        kind: "field",
        definitionKey: "deal.sale_date",
      },
      {
        key: "dealBasics.deliveryDate",
        label: "Delivery date",
        kind: "field",
        definitionKey: "deal.delivery_date",
      },
      { key: "dealBasics.storeId", label: "Store", kind: "field" },
      {
        key: "dealBasics.salePriceCents",
        label: "Vehicle price",
        kind: "field",
        definitionKey: "finance.vehicle_price",
      },
      { key: "dealBasics.lineItems", label: "Additional line items", kind: "collection" },
    ],
  },
  saleVehicle: {
    key: "saleVehicle",
    title: "Sale Vehicle",
    description: "Sale vehicle identity, title, and odometer values used across the packet.",
    order: 20,
    ownedDraftFields: [
      { key: "saleVehicle.vin", label: "VIN", kind: "field", definitionKey: "vehicle.sale.vin" },
      { key: "saleVehicle.year", label: "Year", kind: "field", definitionKey: "vehicle.sale.year" },
      { key: "saleVehicle.make", label: "Make", kind: "field", definitionKey: "vehicle.sale.make" },
      {
        key: "saleVehicle.model",
        label: "Model",
        kind: "field",
        definitionKey: "vehicle.sale.model",
      },
      {
        key: "saleVehicle.stockNumber",
        label: "Stock number",
        kind: "field",
        definitionKey: "vehicle.sale.stock_number",
      },
      {
        key: "saleVehicle.fuelType",
        label: "Fuel type",
        kind: "field",
        definitionKey: "vehicle.fuel_type",
      },
      {
        key: "saleVehicle.odometer",
        label: "Odometer",
        kind: "field",
        definitionKey: "vehicle.sale.odometer",
      },
      {
        key: "saleVehicle.odometerStatus",
        label: "Odometer status",
        kind: "field",
        definitionKey: "vehicle.sale.odometer_status",
      },
      {
        key: "saleVehicle.titleStatus",
        label: "Title status",
        kind: "field",
        definitionKey: "vehicle.sale.title_status",
      },
    ],
  },
  tradeIn: {
    key: "tradeIn",
    title: "Trade-in",
    description: "Trade vehicle and payoff data implied by the packet or current draft state.",
    order: 30,
    ownedDraftFields: [
      {
        key: "tradeIn.vin",
        label: "Trade VIN",
        kind: "field",
        definitionKey: "vehicle.trade_in.vin",
      },
      {
        key: "tradeIn.year",
        label: "Trade year",
        kind: "field",
        definitionKey: "vehicle.trade_in.year",
      },
      {
        key: "tradeIn.make",
        label: "Trade make",
        kind: "field",
        definitionKey: "vehicle.trade_in.make",
      },
      {
        key: "tradeIn.model",
        label: "Trade model",
        kind: "field",
        definitionKey: "vehicle.trade_in.model",
      },
      {
        key: "tradeIn.stockNumber",
        label: "Trade stock number",
        kind: "field",
        definitionKey: "vehicle.trade_in.stock_number",
      },
      {
        key: "tradeIn.odometer",
        label: "Trade odometer",
        kind: "field",
        definitionKey: "vehicle.trade_in.odometer",
      },
      {
        key: "tradeIn.odometerStatus",
        label: "Trade odometer status",
        kind: "field",
        definitionKey: "vehicle.trade_in.odometer_status",
      },
      {
        key: "tradeIn.titleStatus",
        label: "Trade title status",
        kind: "field",
        definitionKey: "vehicle.trade_in.title_status",
      },
      {
        key: "tradeIn.tradeInValueCents",
        label: "Trade-in value",
        kind: "field",
        definitionKey: "finance.trade_allowance",
      },
    ],
  },
  buyers: {
    key: "buyers",
    title: "Primary Buyer",
    description: "Primary buyer identity and business-signer details required by the packet.",
    order: 40,
    ownedDraftFields: BUYER_FIELD_PREFIXES.flatMap((prefix) =>
      createBuyerIdentityOwnedDraftFields(prefix),
    ),
  },
  addresses: {
    key: "addresses",
    title: "Addresses",
    description: "Mailing and registration address requirements grouped by party.",
    order: 50,
    ownedDraftFields: BUYER_FIELD_PREFIXES.flatMap((prefix) =>
      createBuyerAddressOwnedDraftFields(prefix),
    ),
  },
  lenderPayoff: {
    key: "lenderPayoff",
    title: "Lender / payoff",
    description: "Lender, lien, and payoff data required by the packet.",
    order: 60,
    ownedDraftFields: [
      {
        key: "lenderPayoff.lenderName",
        label: "Lender name",
        kind: "field",
        definitionKey: "finance.lender_name",
      },
      {
        key: "lenderPayoff.lenderNumber",
        label: "Lender number",
        kind: "field",
        definitionKey: "finance.lender_number",
      },
      {
        key: "lenderPayoff.lenderPhone",
        label: "Lender phone",
        kind: "field",
        definitionKey: "finance.lender_phone",
      },
      {
        key: "lenderPayoff.address.street",
        label: "Lender street",
        kind: "field",
        definitionKey: "finance.lender_address.street",
      },
      {
        key: "lenderPayoff.address.city",
        label: "Lender city",
        kind: "field",
        definitionKey: "finance.lender_address.city",
      },
      {
        key: "lenderPayoff.address.state",
        label: "Lender state",
        kind: "field",
        definitionKey: "finance.lender_address.state",
      },
      {
        key: "lenderPayoff.address.zipCode",
        label: "Lender ZIP",
        kind: "field",
        definitionKey: "finance.lender_address.zip_code",
      },
      {
        key: "lenderPayoff.lienDate",
        label: "Lien date",
        kind: "field",
        definitionKey: "finance.lien_date",
      },
      {
        key: "lenderPayoff.lienAmountCents",
        label: "Lien amount",
        kind: "field",
        definitionKey: "finance.lien_amount",
      },
    ],
  },
  documentFields: {
    key: "documentFields",
    title: "Document Fields",
    description: "Values required by documents in this packet, grouped by document.",
    order: 75,
    ownedDraftFields: [],
  },
  deliverySigners: {
    key: "deliverySigners",
    title: "Delivery / signers",
    description: "Signer routing and delivery requirements implied by the packet.",
    order: 90,
    ownedDraftFields: [],
  },
}

/** Definition keys that are covered by core section owned draft fields. */
export const COVERED_DEFINITION_KEYS: ReadonlySet<string> = new Set(
  Object.values(DOMAIN_SECTION_DEFAULTS)
    .flatMap((s) => s.ownedDraftFields)
    .map((f) => f.definitionKey)
    .filter((k): k is string => k != null),
)

/** Look up the owned draft field key for a merge-field definition key, if covered. */
export function getDraftFieldKeyForDefinitionKey(
  definitionKey: string,
): PacketAnalysisOwnedDraftFieldKey | null {
  return getOwnedDraftFieldKeyForBinding({
    definitionKey,
    primarySource: "standard",
  })
}

export function getOwnedDraftFieldKeyForBinding(
  binding: Pick<
    MergeFieldBinding,
    "definitionKey" | "partySelector" | "addressType" | "primarySource"
  > & {
    signerRole?: string
  },
): PacketAnalysisOwnedDraftFieldKey | null {
  const lenderFieldKey = getLenderDraftFieldKey(binding.definitionKey)
  if (lenderFieldKey) {
    return lenderFieldKey
  }

  const tradeInFieldKey = getTradeInDraftFieldKey(binding.definitionKey)
  if (tradeInFieldKey) {
    return tradeInFieldKey
  }

  switch (binding.definitionKey) {
    case "deal.sale_date":
      return "dealBasics.saleDate"
    case "deal.delivery_date":
      return "dealBasics.deliveryDate"
    case "finance.vehicle_price":
      return "dealBasics.salePriceCents"
    case "vehicle.sale.vin":
      return "saleVehicle.vin"
    case "vehicle.sale.year":
      return "saleVehicle.year"
    case "vehicle.sale.make":
      return "saleVehicle.make"
    case "vehicle.sale.model":
      return "saleVehicle.model"
    case "vehicle.sale.stock_number":
      return "saleVehicle.stockNumber"
    case "vehicle.fuel_type":
      return "saleVehicle.fuelType"
    case "vehicle.sale.odometer":
      return "saleVehicle.odometer"
    case "vehicle.sale.odometer_status":
      return "saleVehicle.odometerStatus"
    case "vehicle.sale.title_status":
      return "saleVehicle.titleStatus"
    default:
      return getBuyerDraftFieldKey(binding)
  }
}

function getBuyerDraftFieldKey(
  binding: Pick<MergeFieldBinding, "definitionKey" | "partySelector" | "addressType"> & {
    signerRole?: string
  },
): PacketAnalysisOwnedDraftFieldKey | null {
  const buyerPrefix = getBuyerOwnedDraftFieldPrefix(binding.partySelector ?? binding.signerRole)
  if (!buyerPrefix) {
    return null
  }

  switch (binding.definitionKey) {
    case "party.full_name":
      return `${buyerPrefix}.fullName`
    case "party.email":
      return `${buyerPrefix}.email`
    case "party.phone":
      return `${buyerPrefix}.phone`
    case "party.date_of_birth":
      return `${buyerPrefix}.dateOfBirth`
    case "party.drivers_license_number":
      return `${buyerPrefix}.driversLicenseNumber`
    case "party.drivers_license_state":
      return `${buyerPrefix}.driversLicenseState`
    case "party.drivers_license_expiration":
      return `${buyerPrefix}.driversLicenseExpiration`
    case "party.legal_business_name":
      return `${buyerPrefix}.legalBusinessName`
    case "party.ein":
      return `${buyerPrefix}.ein`
    case "party.authorized_signer_name":
      return `${buyerPrefix}.authorizedSignerName`
    case "party.authorized_signer_title":
      return `${buyerPrefix}.authorizedSignerTitle`
    default:
      return getAddressDraftFieldKey(buyerPrefix, binding)
  }
}

function getAddressDraftFieldKey(
  buyerPrefix: PacketAnalysisBuyerDraftFieldPrefix,
  binding: Pick<MergeFieldBinding, "definitionKey" | "addressType">,
): PacketAnalysisOwnedDraftFieldKey | null {
  const baseKey = getAddressBaseKey(buyerPrefix, binding.addressType)
  if (!baseKey) return null

  switch (binding.definitionKey) {
    case "party.address.street":
      return `${baseKey}.street1` as PacketAnalysisOwnedDraftFieldKey
    case "party.address.city":
      return `${baseKey}.city` as PacketAnalysisOwnedDraftFieldKey
    case "party.address.state":
      return `${baseKey}.state` as PacketAnalysisOwnedDraftFieldKey
    case "party.address.zip_code":
      return `${baseKey}.zipCode` as PacketAnalysisOwnedDraftFieldKey
    default:
      return null
  }
}

function getLenderDraftFieldKey(definitionKey: string): PacketAnalysisOwnedDraftFieldKey | null {
  switch (definitionKey) {
    case "finance.lender_name":
    case "manual.lienholder_name":
      return "lenderPayoff.lenderName"
    case "finance.lender_number":
    case "manual.lender_number":
      return "lenderPayoff.lenderNumber"
    case "finance.lender_phone":
      return "lenderPayoff.lenderPhone"
    case "finance.lender_address.street":
      return "lenderPayoff.address.street"
    case "finance.lender_address.city":
      return "lenderPayoff.address.city"
    case "finance.lender_address.state":
      return "lenderPayoff.address.state"
    case "finance.lender_address.zip_code":
      return "lenderPayoff.address.zipCode"
    case "finance.lien_date":
      return "lenderPayoff.lienDate"
    case "finance.lien_amount":
      return "lenderPayoff.lienAmountCents"
    default:
      return null
  }
}

function getTradeInDraftFieldKey(definitionKey: string): PacketAnalysisOwnedDraftFieldKey | null {
  switch (definitionKey) {
    case "vehicle.trade_in.vin":
      return "tradeIn.vin"
    case "vehicle.trade_in.year":
      return "tradeIn.year"
    case "vehicle.trade_in.make":
      return "tradeIn.make"
    case "vehicle.trade_in.model":
      return "tradeIn.model"
    case "vehicle.trade_in.stock_number":
      return "tradeIn.stockNumber"
    case "vehicle.trade_in.odometer":
      return "tradeIn.odometer"
    case "vehicle.trade_in.odometer_status":
      return "tradeIn.odometerStatus"
    case "vehicle.trade_in.title_status":
      return "tradeIn.titleStatus"
    case "finance.trade_allowance":
      return "tradeIn.tradeInValueCents"
    default:
      return null
  }
}

function getAddressBaseKey(
  buyerPrefix: PacketAnalysisBuyerDraftFieldPrefix,
  addressType: MergeFieldBinding["addressType"],
): `${PacketAnalysisBuyerDraftFieldPrefix}.${PacketAnalysisBuyerAddressSectionKey}` | null {
  switch (addressType ?? "mailing") {
    case "mailing":
      return `${buyerPrefix}.mailingAddress`
    case "registration":
      return `${buyerPrefix}.registrationAddress`
    case "residence":
      return `${buyerPrefix}.residenceAddress`
    default:
      return null
  }
}

function getBuyerOwnedDraftFieldPrefix(
  partySelector: string | undefined,
): PacketAnalysisBuyerDraftFieldPrefix | null {
  if (!partySelector) {
    return "primaryBuyer"
  }

  const buyerIndex = getBuyerSignerIndex(partySelector)
  if (buyerIndex === undefined) {
    return null
  }

  return getBuyerOwnedDraftFieldPrefixBySignerIndex(buyerIndex)
}

export function getBuyerOwnedDraftFieldPrefixBySignerIndex(
  buyerIndex: number,
): PacketAnalysisBuyerDraftFieldPrefix | null {
  return BUYER_FIELD_PREFIXES[buyerIndex] ?? null
}

/** Look up the owned draft field key for a merge-field definition key, if covered. */
export function getDraftFieldKeyForDefinitionKeyLegacyFallback(
  definitionKey: string,
): PacketAnalysisOwnedDraftFieldKey | null {
  for (const section of Object.values(DOMAIN_SECTION_DEFAULTS)) {
    for (const field of section.ownedDraftFields) {
      if (field.definitionKey === definitionKey) return field.key
    }
  }
  return null
}
